import { useState, useRef, useEffect, useCallback } from 'react';

const STORAGE_KEY_QUEUES = 'selfieFan_queues_v1';
const STORAGE_KEY_INDEX  = 'selfieFan_indexes_v1';

export const useSelfiePlayer = (actions, showMessage, onItemPlayed) => {

  const [isLive, setIsLive]       = useState(false);
  const [playingTab, setPlayingTab] = useState(null); 
  const [activeTab, setActiveTab] = useState('FC');
  const [autoPlayIntervalFC, setAutoPlayIntervalFC] = useState(3000); 

  const [queues, setQueues] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_QUEUES);
      const parsed = saved ? JSON.parse(saved) : {};
      return { FC: [], IA: [], ST: [], ...parsed };
    } catch (error) { return { FC: [], IA: [], ST: [] }; }
  });

  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_INDEX);
      const parsed = saved ? JSON.parse(saved) : {};
      return { FC: 0, IA: 0, ST: 0, ...parsed };
    } catch (error) { return { FC: 0, IA: 0, ST: 0 }; }
  });

  const [iaSequenceStep, setIaSequenceStep] = useState(0); 
  const [projectionStatus, setProjectionStatus] = useState("Mostrando: Nada");
  const [progress, setProgress] = useState(0);

  const startTimeRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_QUEUES, JSON.stringify(queues));
      localStorage.setItem(STORAGE_KEY_INDEX, JSON.stringify(currentIndex));
    } catch (e) {}
  }, [queues, currentIndex]);
  
  const addToQueue = useCallback((type, item) => {
    if (!item) return;
    const queueItem = { ...item, _queueId: Date.now() + Math.random() };
    setQueues(prev => ({ ...prev, [type]: [...prev[type], queueItem] }));
  }, []);

  const removeFromQueue = useCallback((type, indexToRemove) => {
    setQueues(prev => {
      const newQueue = prev[type].filter((_, idx) => idx !== indexToRemove);
      setCurrentIndex(curr => {
        let newIdx = curr[type];
        if (indexToRemove < newIdx) newIdx = newIdx - 1;
        else if (indexToRemove === newIdx && newIdx >= newQueue.length) newIdx = Math.max(0, newQueue.length - 1);
        return { ...curr, [type]: newIdx };
      });
      return { ...prev, [type]: newQueue };
    });
  }, []);

  const clearQueue = useCallback((type) => {
    if (playingTab === type) { setPlayingTab(null); setProgress(0); startTimeRef.current = null; }
    setQueues(prev => ({ ...prev, [type]: [] }));
    setCurrentIndex(prev => ({ ...prev, [type]: 0 }));
    if (type === 'IA') setIaSequenceStep(0);
  }, [playingTab]);

  const reorderQueue = useCallback((type, sourceIndex, destIndex) => {
    if (playingTab === type) setPlayingTab(null);
    setQueues(prev => {
      const list = [...prev[type]];
      if (destIndex < 0 || destIndex >= list.length) return prev;
      const [removed] = list.splice(sourceIndex, 1);
      list.splice(destIndex, 0, removed);
      setCurrentIndex(curr => {
        let newIdx = curr[type];
        if (curr[type] === sourceIndex) newIdx = destIndex;
        else if (curr[type] === destIndex) newIdx = sourceIndex;
        return { ...curr, [type]: newIdx };
      });
      return { ...prev, [type]: list };
    });
  }, [playingTab]);

  const refreshItemInQueue = useCallback((id, newThumbUrl, newRotation) => {
    setQueues(prev => {
        const updateList = (list) => list.map(item => {
            if (item.id === id) return { ...item, thumbUrl: newThumbUrl, rotation: newRotation };
            if (item.img1_id === id) return { ...item, img1_thumb: newThumbUrl }; 
            if (item.img2_id === id) return { ...item, img2_thumb: newThumbUrl }; 
            return item;
        });

        return {
            FC: updateList(prev.FC),
            IA: updateList(prev.IA),
            ST: updateList(prev.ST)
        };
    });
  }, []);

  const updateCurrentItemAnimation = useCallback((step, animValue) => {
    setQueues(prev => {
      const type = 'IA';
      const list = [...prev[type]];
      const idx = currentIndex[type];
      if (!list[idx]) return prev;
      const updatedItem = { ...list[idx] };
      if (step === 1) updatedItem.anim1 = animValue;
      if (step === 2) updatedItem.anim2 = animValue;
      list[idx] = updatedItem;
      return { ...prev, [type]: list };
    });
  }, [currentIndex]);

  const projectCurrentItem = useCallback(async (type, index, specificIaStep = null) => {
    const queue = queues[type];
    const item = queue[index];

    if (!item) { setProjectionStatus("Esperando contenido..."); return; }

    if (type === 'FC' || type === 'ST') setProjectionStatus(`Mostrando: Foto FC #${item.index || item.id}`);
    else {
        const step = specificIaStep || 1;
        setIaSequenceStep(step);
        setProjectionStatus(step === 1 ? "Mostrando: IA Paso 1" : "Mostrando: IA Paso 2");
    }

    if (!isLive) return;

    try {
      if (type === 'FC' || type === 'ST') {
        await actions.sendToScreenFC(item);
        if (onItemPlayed) onItemPlayed(item.id); 
      } else if (type === 'IA') {
        const step = specificIaStep || 1;
        let payload = [];
        const a1 = item.anim1 ?? 0;
        const a2 = item.anim2 ?? 0;

        if (step === 1) {
           payload.push({ id: item.img1_id, animation: a1 });
           if (onItemPlayed) onItemPlayed(item.img1_id); 
        } else {
           payload.push({ id: item.img1_id, animation: a1 });
           payload.push({ id: item.img2_id, animation: a2 });
           if (onItemPlayed) { onItemPlayed(item.img1_id); onItemPlayed(item.img2_id); } 
        }
        await actions.sendToScreenIA(payload);
      }
    } catch (error) {
      console.error(error);
      setPlayingTab(null);
      showMessage({ type: 'error', text: 'Error de proyección.' });
    }
  }, [isLive, queues, actions, showMessage, onItemPlayed]); 
  
  const jumpToItem = useCallback((type, index) => {
    if (playingTab === type) { setPlayingTab(null); setProgress(0); startTimeRef.current = null; }
    setCurrentIndex(prev => ({ ...prev, [type]: index }));
    if (type === 'IA') setIaSequenceStep(0);

    if (isLive && type === activeTab) {
        if (type === 'FC' || type === 'ST') projectCurrentItem(type, index);
    }
  }, [playingTab, isLive, activeTab, projectCurrentItem]);

  const playNext = useCallback((targetType = null) => {
    const type = targetType || playingTab || activeTab; 
    const currentQ = queues[type];
    const currentIdx = currentIndex[type];

    if (type === 'IA') setIaSequenceStep(1);

    if (currentIdx + 1 >= currentQ.length) {
      setPlayingTab(null); 
      setProgress(0);
      showMessage({ type: 'info', text: 'Fin de la lista.' });
      return;
    }

    const nextIdx = currentIdx + 1;
    setCurrentIndex(prev => ({ ...prev, [type]: nextIdx }));
    
    setProgress(0);
    startTimeRef.current = performance.now();

    if (isLive) projectCurrentItem(type, nextIdx, 1);
  }, [playingTab, activeTab, queues, currentIndex, isLive, showMessage, projectCurrentItem]);

  const playPrev = useCallback(() => {
    if (playingTab) setPlayingTab(null);
    const type = activeTab;
    if (currentIndex[type] > 0) {
      const prevIdx = currentIndex[type] - 1;
      setCurrentIndex(prev => ({ ...prev, [type]: prevIdx }));
      if (type === 'IA') setIaSequenceStep(1);
      if (isLive) projectCurrentItem(type, prevIdx, 1);
    }
  }, [activeTab, currentIndex, isLive, projectCurrentItem, playingTab]);

  useEffect(() => {
    if (!playingTab || !isLive) {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        setProgress(0);
        startTimeRef.current = null;
        return;
    }

    const duration = (playingTab === 'FC' || playingTab === 'ST') ? autoPlayIntervalFC : (iaSequenceStep === 1 ? 4000 : 6000);

    if (startTimeRef.current === null) startTimeRef.current = performance.now();

    const loop = (timestamp) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp;
        const elapsed = timestamp - startTimeRef.current;
        let pct = (elapsed / duration) * 100;
        if (pct > 100) pct = 100;
        setProgress(pct);

        if (elapsed >= duration) {
            startTimeRef.current = null;
            setProgress(0);
            if (playingTab === 'FC' || playingTab === 'ST') playNext(playingTab); 
            else {
                if (iaSequenceStep === 1) projectCurrentItem('IA', currentIndex.IA, 2);
                else playNext('IA');
            }
        } else {
            animationFrameRef.current = requestAnimationFrame(loop);
        }
    };
    animationFrameRef.current = requestAnimationFrame(loop);
    return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
  }, [playingTab, isLive, iaSequenceStep, autoPlayIntervalFC, currentIndex, playNext, projectCurrentItem]);

  const toggleLive = (shouldBeLive) => {
    if (shouldBeLive) {
       if (queues[activeTab].length === 0) {
         showMessage({ type: 'warning', text: 'Fila vacía.' });
         return false;
       }
       setIsLive(true);
       showMessage({ type: 'success', text: 'EN VIVO' });
       setTimeout(() => {
         const idx = currentIndex[activeTab];
         projectCurrentItem(activeTab, idx, 1);
       }, 0);
       return true;
    } else {
       setIsLive(false);
       setPlayingTab(null);
       setProgress(0);
       showMessage({ type: 'info', text: 'TRANSMISIÓN DETENIDA' });
       return true;
    }
  };

  const emergencyStop = async () => {
    setIsLive(false);
    setPlayingTab(null);
    setProgress(0);
    setProjectionStatus("Mostrando: Pantalla Limpia");
    await actions.clearScreen();
    showMessage({ type: 'success', text: 'PANTALLA LIMPIA' });
  };

  const togglePlay = () => {
      if (!isLive) { showMessage({ type: 'warning', text: 'Activa LIVE primero.' }); return; }
      if (playingTab === activeTab) {
          setPlayingTab(null);
          startTimeRef.current = null;
      } else {
          setPlayingTab(activeTab);
          startTimeRef.current = null;
          const curIdx = currentIndex[activeTab];
          projectCurrentItem(activeTab, curIdx, 1);
      }
  };

  const triggerIaStep = (step) => {
      if (playingTab === 'FC' || playingTab === 'ST') setPlayingTab(null); 
      if (!isLive) { showMessage({ type: 'warning', text: 'Activa LIVE primero.' }); return; }
      projectCurrentItem('IA', currentIndex.IA, step);
  };

  return {
    isLive, playingTab, activeTab, queues, currentIndex, iaSequenceStep, projectionStatus, progress, autoPlayIntervalFC,
    setActiveTab, togglePlay, setAutoPlayIntervalFC, addToQueue, removeFromQueue, clearQueue, 
    reorderQueue, updateCurrentItemAnimation, refreshItemInQueue, 
    playNext, playPrev, jumpToItem, triggerIaStep, toggleLive, emergencyStop
  };
};









// import { useState, useRef, useEffect, useCallback } from 'react';

// // Constantes de almacenamiento
// const STORAGE_KEY_QUEUES = 'selfieFan_queues_v1';
// const STORAGE_KEY_INDEX  = 'selfieFan_indexes_v1';

// /**
//  * useSelfiePlayer
//  * @param {Object} actions - Funciones para comunicar con backend
//  * @param {Function} showMessage - Para mostrar toasts
//  * @param {Function} onItemPlayed - (NUEVO) Callback que se ejecuta cuando una imagen se proyecta
//  */
// export const useSelfiePlayer = (actions, showMessage, onItemPlayed) => {

//   // ==========================================
//   // 1. ESTADOS
//   // ==========================================
//   const [isLive, setIsLive]       = useState(false);
//   const [playingTab, setPlayingTab] = useState(null); 
//   const [activeTab, setActiveTab] = useState('FC');
//   const [autoPlayIntervalFC, setAutoPlayIntervalFC] = useState(3000); 

//   // Carga inicial perezosa del LocalStorage
//   const [queues, setQueues] = useState(() => {
//     try {
//       const saved = localStorage.getItem(STORAGE_KEY_QUEUES);
//       return saved ? JSON.parse(saved) : { FC: [], IA: [] };
//     } catch (error) { return { FC: [], IA: [] }; }
//   });

//   const [currentIndex, setCurrentIndex] = useState(() => {
//     try {
//       const saved = localStorage.getItem(STORAGE_KEY_INDEX);
//       return saved ? JSON.parse(saved) : { FC: 0, IA: 0 };
//     } catch (error) { return { FC: 0, IA: 0 }; }
//   });

//   const [iaSequenceStep, setIaSequenceStep] = useState(0); 
//   const [projectionStatus, setProjectionStatus] = useState("Mostrando: Nada");
//   const [progress, setProgress] = useState(0);

//   const startTimeRef = useRef(null);
//   const animationFrameRef = useRef(null);

//   // Persistencia automática
//   useEffect(() => {
//     try {
//       localStorage.setItem(STORAGE_KEY_QUEUES, JSON.stringify(queues));
//       localStorage.setItem(STORAGE_KEY_INDEX, JSON.stringify(currentIndex));
//     } catch (e) {}
//   }, [queues, currentIndex]);


//   // ==========================================
//   // 2. GESTIÓN DE COLAS
//   // ==========================================
  
//   const addToQueue = useCallback((type, item) => {
//     if (!item) return;
//     const queueItem = { ...item, _queueId: Date.now() + Math.random() };
//     setQueues(prev => ({ ...prev, [type]: [...prev[type], queueItem] }));
//   }, []);

//   const removeFromQueue = useCallback((type, indexToRemove) => {
//     setQueues(prev => {
//       const newQueue = prev[type].filter((_, idx) => idx !== indexToRemove);
//       setCurrentIndex(curr => {
//         let newIdx = curr[type];
//         if (indexToRemove < newIdx) newIdx = newIdx - 1;
//         else if (indexToRemove === newIdx && newIdx >= newQueue.length) newIdx = Math.max(0, newQueue.length - 1);
//         return { ...curr, [type]: newIdx };
//       });
//       return { ...prev, [type]: newQueue };
//     });
//   }, []);

//   const clearQueue = useCallback((type) => {
//     if (playingTab === type) { setPlayingTab(null); setProgress(0); startTimeRef.current = null; }
//     setQueues(prev => ({ ...prev, [type]: [] }));
//     setCurrentIndex(prev => ({ ...prev, [type]: 0 }));
//     if (type === 'IA') setIaSequenceStep(0);
//   }, [playingTab]);

//   const reorderQueue = useCallback((type, sourceIndex, destIndex) => {
//     if (playingTab === type) setPlayingTab(null);
//     setQueues(prev => {
//       const list = [...prev[type]];
//       if (destIndex < 0 || destIndex >= list.length) return prev;
//       const [removed] = list.splice(sourceIndex, 1);
//       list.splice(destIndex, 0, removed);
//       setCurrentIndex(curr => {
//         let newIdx = curr[type];
//         if (curr[type] === sourceIndex) newIdx = destIndex;
//         else if (curr[type] === destIndex) newIdx = sourceIndex;
//         return { ...curr, [type]: newIdx };
//       });
//       return { ...prev, [type]: list };
//     });
//   }, [playingTab]);

//   // NUEVO: Actualiza un item en la cola (útil para reflejar rotación)
//   const refreshItemInQueue = useCallback((id, newThumbUrl, newRotation) => {
//     setQueues(prev => {
//         // Actualizamos en ambas colas por si acaso
//         const updateList = (list) => list.map(item => {
//             // Comparamos por ID original, no por _queueId
//             // Para IA puede ser img1_id o img2_id
//             if (item.id === id) return { ...item, thumbUrl: newThumbUrl, rotation: newRotation };
//             if (item.img1_id === id) return { ...item, img1_thumb: newThumbUrl }; // Actualizar thumb set IA
//             if (item.img2_id === id) return { ...item, img2_thumb: newThumbUrl }; 
//             return item;
//         });

//         return {
//             FC: updateList(prev.FC),
//             IA: updateList(prev.IA)
//         };
//     });
//   }, []);

//   const updateCurrentItemAnimation = useCallback((step, animValue) => {
//     setQueues(prev => {
//       const type = 'IA';
//       const list = [...prev[type]];
//       const idx = currentIndex[type];
//       if (!list[idx]) return prev;
//       const updatedItem = { ...list[idx] };
//       if (step === 1) updatedItem.anim1 = animValue;
//       if (step === 2) updatedItem.anim2 = animValue;
//       list[idx] = updatedItem;
//       return { ...prev, [type]: list };
//     });
//   }, [currentIndex]);


//   // ==========================================
//   // 3. PROYECCIÓN
//   // ==========================================
//   const projectCurrentItem = useCallback(async (type, index, specificIaStep = null) => {
//     const queue = queues[type];
//     const item = queue[index];

//     if (!item) { setProjectionStatus("Esperando contenido..."); return; }

//     if (type === 'FC') setProjectionStatus(`Mostrando: Foto FC #${item.index || item.id}`);
//     else {
//         const step = specificIaStep || 1;
//         setIaSequenceStep(step);
//         setProjectionStatus(step === 1 ? "Mostrando: IA Paso 1" : "Mostrando: IA Paso 2");
//     }

//     if (!isLive) return;

//     try {
//       if (type === 'FC') {
//         await actions.sendToScreenFC(item);
//         // NUEVO: Avisar al padre que se mostró esta foto
//         if (onItemPlayed) onItemPlayed(item.id); 
//       } else if (type === 'IA') {
//         const step = specificIaStep || 1;
//         let payload = [];
//         const a1 = item.anim1 ?? 0;
//         const a2 = item.anim2 ?? 0;

//         if (step === 1) {
//            payload.push({ id: item.img1_id, animation: a1 });
//            if (onItemPlayed) onItemPlayed(item.img1_id); // Avisar foto 1 mostrada
//         } else {
//            payload.push({ id: item.img1_id, animation: a1 });
//            payload.push({ id: item.img2_id, animation: a2 });
//            if (onItemPlayed) { onItemPlayed(item.img1_id); onItemPlayed(item.img2_id); } // Avisar ambas
//         }
//         await actions.sendToScreenIA(payload);
//       }
//     } catch (error) {
//       console.error(error);
//       setPlayingTab(null);
//       showMessage({ type: 'error', text: 'Error de proyección.' });
//     }
//   }, [isLive, queues, actions, showMessage, onItemPlayed]); // Agregamos onItemPlayed a dependencias


//   // ==========================================
//   // 4. CONTROL PLAYBACK
//   // ==========================================
  
//   const jumpToItem = useCallback((type, index) => {
//     if (playingTab === type) { setPlayingTab(null); setProgress(0); startTimeRef.current = null; }
//     setCurrentIndex(prev => ({ ...prev, [type]: index }));
//     if (type === 'IA') setIaSequenceStep(0);

//     if (isLive && type === activeTab) {
//         if (type === 'FC') projectCurrentItem(type, index);
//     }
//   }, [playingTab, isLive, activeTab, projectCurrentItem]);

//   const playNext = useCallback((targetType = null) => {
//     const type = targetType || playingTab || activeTab; 
//     const currentQ = queues[type];
//     const currentIdx = currentIndex[type];

//     if (type === 'IA') setIaSequenceStep(1);

//     if (currentIdx + 1 >= currentQ.length) {
//       setPlayingTab(null); 
//       setProgress(0);
//       showMessage({ type: 'info', text: 'Fin de la lista.' });
//       return;
//     }

//     const nextIdx = currentIdx + 1;
//     setCurrentIndex(prev => ({ ...prev, [type]: nextIdx }));
    
//     setProgress(0);
//     startTimeRef.current = performance.now();

//     if (isLive) projectCurrentItem(type, nextIdx, 1);
//   }, [playingTab, activeTab, queues, currentIndex, isLive, showMessage, projectCurrentItem]);

//   const playPrev = useCallback(() => {
//     if (playingTab) setPlayingTab(null);
//     const type = activeTab;
//     if (currentIndex[type] > 0) {
//       const prevIdx = currentIndex[type] - 1;
//       setCurrentIndex(prev => ({ ...prev, [type]: prevIdx }));
//       if (type === 'IA') setIaSequenceStep(1);
//       if (isLive) projectCurrentItem(type, prevIdx, 1);
//     }
//   }, [activeTab, currentIndex, isLive, projectCurrentItem, playingTab]);

//   // ==========================================
//   // 5. MOTOR DE TIEMPO
//   // ==========================================
//   useEffect(() => {
//     if (!playingTab || !isLive) {
//         if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
//         setProgress(0);
//         startTimeRef.current = null;
//         return;
//     }

//     const duration = playingTab === 'FC' ? autoPlayIntervalFC : (iaSequenceStep === 1 ? 4000 : 6000);

//     if (startTimeRef.current === null) startTimeRef.current = performance.now();

//     const loop = (timestamp) => {
//         if (!startTimeRef.current) startTimeRef.current = timestamp;
//         const elapsed = timestamp - startTimeRef.current;
//         let pct = (elapsed / duration) * 100;
//         if (pct > 100) pct = 100;
//         setProgress(pct);

//         if (elapsed >= duration) {
//             startTimeRef.current = null;
//             setProgress(0);
//             if (playingTab === 'FC') playNext('FC'); 
//             else {
//                 if (iaSequenceStep === 1) projectCurrentItem('IA', currentIndex.IA, 2);
//                 else playNext('IA');
//             }
//         } else {
//             animationFrameRef.current = requestAnimationFrame(loop);
//         }
//     };
//     animationFrameRef.current = requestAnimationFrame(loop);
//     return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
//   }, [playingTab, isLive, iaSequenceStep, autoPlayIntervalFC, currentIndex, playNext, projectCurrentItem]);

//   // ==========================================
//   // 6. ACCIONES PÚBLICAS
//   // ==========================================
//   const toggleLive = (shouldBeLive) => {
//     if (shouldBeLive) {
//        if (queues[activeTab].length === 0) {
//          showMessage({ type: 'warning', text: 'Fila vacía.' });
//          return false;
//        }
//        setIsLive(true);
//        showMessage({ type: 'success', text: 'EN VIVO' });
//        setTimeout(() => {
//          const idx = currentIndex[activeTab];
//          projectCurrentItem(activeTab, idx, 1);
//        }, 0);
//        return true;
//     } else {
//        setIsLive(false);
//        setPlayingTab(null);
//        setProgress(0);
//        showMessage({ type: 'info', text: 'TRANSMISIÓN DETENIDA' });
//        return true;
//     }
//   };

//   const emergencyStop = async () => {
//     setIsLive(false);
//     setPlayingTab(null);
//     setProgress(0);
//     setProjectionStatus("Mostrando: Pantalla Limpia");
//     await actions.clearScreen();
//     showMessage({ type: 'success', text: 'PANTALLA LIMPIA' });
//   };

//   const togglePlay = () => {
//       if (!isLive) { showMessage({ type: 'warning', text: 'Activa LIVE primero.' }); return; }
//       if (playingTab === activeTab) {
//           setPlayingTab(null);
//           startTimeRef.current = null;
//       } else {
//           setPlayingTab(activeTab);
//           startTimeRef.current = null;
//           const curIdx = currentIndex[activeTab];
//           projectCurrentItem(activeTab, curIdx, 1);
//       }
//   };

//   const triggerIaStep = (step) => {
//       if (playingTab === 'FC') setPlayingTab(null); 
//       if (!isLive) { showMessage({ type: 'warning', text: 'Activa LIVE primero.' }); return; }
//       projectCurrentItem('IA', currentIndex.IA, step);
//   };

//   return {
//     isLive, playingTab, activeTab, queues, currentIndex, iaSequenceStep, projectionStatus, progress, autoPlayIntervalFC,
//     setActiveTab, togglePlay, setAutoPlayIntervalFC, addToQueue, removeFromQueue, clearQueue, 
//     reorderQueue, updateCurrentItemAnimation, refreshItemInQueue, // Nueva función expuesta
//     playNext, playPrev, jumpToItem, triggerIaStep, toggleLive, emergencyStop
//   };
// };